<script setup lang="ts">
import { computed, inject, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import {
  useKapitaloveVydavkyStore,
  useOsobneVydavkyStore,
  useOstatneVydavkyStore,
  useRozpocetStore,
  useSluzbyStore,
  useTovaryStore,
  useZjednoduseneVydavkyStore,
} from '/@src/stores/rozpocet'
import { formatMoney, formatPercentage } from '/@src/utils/moneyFormatter'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'

const { t } = useI18n()
const steps = useSteps()
const rozpocet = useRozpocetStore()
const tovaryStore = useTovaryStore()
const sluzbyStore = useSluzbyStore()
const osobneVydavkyStore = useOsobneVydavkyStore()
const kapitaloveVydavkyStore = useKapitaloveVydavkyStore()
const ostatneVydavkyStore = useOstatneVydavkyStore()
const zjednoduseneVydavkyStore = useZjednoduseneVydavkyStore()

const celkovaZiadanaSumaPM = computed(() => rozpocet.celkovaZiadanaSumaPM)
const finalSum = computed(() => {
  return (
    (celkovaZiadanaSumaPM.value.sumaEurBezDph || 0) +
    (celkovaZiadanaSumaPM.value.dphEur || 0)
  )
})
const ibaDph = computed(() => celkovaZiadanaSumaPM.value.dphEur)
const bezDph = computed(() => celkovaZiadanaSumaPM.value.sumaEurBezDph)
const opravneneVydavky = computed(() => rozpocet.celkoveOpravneneVydavky.sumaEur)
const ineZdroje = computed(() => rozpocet.ineZdrojeFinancovania.sumaEur)
const vlastneZdroje = computed(() => rozpocet.vlastneZdrojeZiadatela.sumaEur)
const statnyRozpocet = computed(() => rozpocet.statnyRozpocetSumar.sumaEur)
const podielFinancovaniaPMPerc = computed(() => rozpocet.podielFinancovaniaPMPerc.percent)

const stavPodana = inject<boolean>('stavPodana', false)

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.rozpocet'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.step9.mainHeading')
)
</script>

<template>
  <IDSKRow v-if="finalSum">
    <IDSKCol>
      <IDSKHeading as="h2" variant="l" class="mb-7">
        {{ heading }}
      </IDSKHeading>

      <template
        v-for="(store, id) in {
          tovary: tovaryStore,
          sluzby: sluzbyStore,
          osobneNaklady: osobneVydavkyStore,
          kapitaloveVydavky: kapitaloveVydavkyStore,
          ostatne: ostatneVydavkyStore,
        }"
      >
        <RozpocetVydavky :store="store">
          <template #title>
            {{ t('zoppm.step9.SKUTOCNY.heading') }}:
            {{ t(`zoppm.zhrnutie.rozpocet.${id}`) }}
          </template>
        </RozpocetVydavky>
      </template>

      <RozpocetVydavky :store="zjednoduseneVydavkyStore">
        <template #title>
          {{ t('zoppm.step9.ZJEDNODUSENY.heading') }}
        </template>
      </RozpocetVydavky>

      <IDSKHeading as="h3" variant="m" class="mb-6">
        {{ t('zoppm.step9.section3.heading') }}
      </IDSKHeading>
      <DisplayList variant="inline" inline-layout="one-half" class="mb-2">
        <template #default>
          <template v-if="finalSum">
            <dt class="govuk-!-font-weight-bold mb-2">
              {{ t('zoppm.step9.section3.table.finalSum') }}
            </dt>
            <dd class="govuk-!-font-weight-bold mb-2">
              {{ formatMoney(finalSum) }}
            </dd>
          </template>
          <template v-if="bezDph">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.finalSumNoTax') }}
            </dt>
            <dd class="mb-2">{{ formatMoney(bezDph) }}</dd>
          </template>
          <template v-if="ibaDph">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.finalSumOnlyTax') }}
            </dt>
            <dd class="mb-2">{{ formatMoney(ibaDph) }}</dd>
          </template>

          <template v-if="vlastneZdroje">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.sources') }}
            </dt>
            <dd class="mb-2">{{ formatMoney(vlastneZdroje) }}</dd>
          </template>

          <template v-if="opravneneVydavky">
            <dt class="govuk-!-font-weight-bold mb-2">
              {{ t('zoppm.step9.section3.table.finalExpenses') }}
            </dt>
            <dd class="govuk-!-font-weight-bold mb-2">
              {{ formatMoney(opravneneVydavky) }}
            </dd>
          </template>

          <template v-if="statnyRozpocet">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.statnyRozpocet') }}
            </dt>
            <dd class="mb-2">{{ formatMoney(statnyRozpocet) }}</dd>
          </template>

          <template v-if="ineZdroje">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.otherSources') }}
            </dt>
            <dd class="mb-2">{{ formatMoney(ineZdroje) }}</dd>
          </template>

          <template v-if="podielFinancovaniaPMPerc">
            <dt class="mb-2">
              {{ t('zoppm.step9.section3.table.financeShare') }}
            </dt>
            <dd class="mb-2">{{ formatPercentage(podielFinancovaniaPMPerc, 4) }}</dd>
          </template>
        </template>
      </DisplayList>

      <div v-if="!stavPodana" class="d-flex align-items-right">
        <IDSKButton
          variant="secondary"
          type="button"
          class="mb-0"
          :data-test-id="`zoppm-rozpocet-zhrnutie-edit`"
          @click="
            () =>
              steps.setStep(NazvyKrokov['rozpocet'], {
                ref: NazvyKrokov['zhrnutie'],
              })
          "
        >
          {{ t('zoppm.zhrnutie.editBtn') }}
        </IDSKButton>
      </div>

      <hr class="mb-8 mt-8" />
    </IDSKCol>
  </IDSKRow>
</template>

<style scoped lang="scss">
:deep(.display-list) {
  grid-template-columns: 1fr auto;

  dd {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
